import React from 'react';
import { useLazyQuery } from 'apollo-client-ppc';
import { Currency } from '@atlassian/cssxp-ccp-types';
import { abgGraphqlClient as graphClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import {
  FetchQuotesForTransactionAccountQuery,
  FetchQuotesForTransactionAccountQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-sxp';
import { downloadAsCSV } from '@css-xp/ppc-utils';
import {
  CommerceAddress,
  EndCustomerContact,
  FilterOption,
  SortType,
} from '@css-xp/graphtypes/src/generated-types-new';
import { DiscountType, getTotalPartnerMarginAmount } from '@css-xp/ppc-quotes-page';
import { formatAddressLabel } from '@css-xp/base';
import { fetchQuotesForTransactionAccount } from './queries/fetch-quotes-for-txa';
import { fetchIGPurchaseOrderInPPC } from './queries/fetch-ig-purchase-order';
import { COLUMNS, QuoteTableProps } from './quote-table';
import { useFetchQuoteContactsFromContactService, useFetchEccFromContactService } from '@css-xp/contact-resources';

export type UseQuoteListCSVProps = Partial<QuoteTableProps['dataState']> & {
  filterOptions?: FilterOption;
  txaId?: string;
  sortOptions?: SortType;
};

export const QUOTE_NAME = 'Quote name';

export const useQuotesListCSV = ({ data, totalCount = 0, filterOptions, txaId, sortOptions }: UseQuoteListCSVProps) => {
  const [quotesListLoading, setQuotesListLoading] = React.useState(false);
  const sxpClient = React.useMemo(() => graphClient(ProxyGraphClients.SXP), []);
  const [loadQuotesList] = useLazyQuery<
    FetchQuotesForTransactionAccountQuery,
    FetchQuotesForTransactionAccountQueryVariables
  >(fetchQuotesForTransactionAccount, {
    variables: {
      filterOptions,
      searchOptions: { first: totalCount, sort: sortOptions },
    },
    client: sxpClient as any,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
  });

  const { getQuoteContactDetailsforCSV } = useFetchQuoteContactsFromContactService();
  const { getECCDetailsForCSV } = useFetchEccFromContactService();

  const getPurchaseOrderNumber = async (invoiceGroupId?: string) => {
    if (!invoiceGroupId) {
      return null;
    }

    const { data } = await graphClient().query({
      query: fetchIGPurchaseOrderInPPC,
      variables: {
        id: invoiceGroupId,
        txaId,
      },
    });

    return data?.invoiceGroup?.purchaseOrder?.number ?? '-';
  };

  const getQuoteContact = async (quoteId: string, quoteContact: string) => {
    if (txaId) {
      const quoteContactData = await getQuoteContactDetailsforCSV(quoteId, txaId);
      return quoteContactData?.[0]?.email ?? '-';
    }
    return quoteContact ?? '-';
  };

  const getQuoteCustomerContacts = async (invoiceGroupId: string, txaId: string) => {
    const quoteCustomerContact = await getECCDetailsForCSV(invoiceGroupId, txaId);
    const eccDetails = quoteCustomerContact?.map((item: EndCustomerContact) => item?.email);
    return eccDetails ? eccDetails.join(', ') : '-';
  };

  const getColumns = () => {
    const quoteCSVHeader = [
      COLUMNS.QUOTE_ID,
      COLUMNS.QUOTE_NUMBER,
      QUOTE_NAME,
      COLUMNS.STATUS,
      COLUMNS.PARTNER_MARGIN,
      COLUMNS.CREATED,
      COLUMNS.EXPIRY_DATE,
      COLUMNS.LAST_UPDATED,
      COLUMNS.QUOTE_TOTAL,
      COLUMNS.COMPANY_NAME,
      COLUMNS.SOLD_TO_ADDRESS,
      COLUMNS.CUSTOMER_CONTACTS,
      COLUMNS.PO_NUMBER,
      COLUMNS.QUOTE_CONTACT,
      COLUMNS.CREATED_BY,
      COLUMNS.UPDATED_BY,
    ];

    return quoteCSVHeader;
  };

  const downloadQuotesListCSV = async () => {
    let quotesList = data;
    if (totalCount > 8) {
      setQuotesListLoading(true);
      const { data } = await loadQuotesList();
      quotesList = data?.quotes?.nodes ?? [];
    }

    let result: string[] = [];
    const promises = quotesList?.map(async (node: any) => {
      const billingGroupId = node?.billingGroupId;
      if (txaId && billingGroupId) {
        return await getQuoteCustomerContacts(billingGroupId, txaId);
      }
    });
    result = await Promise.all(promises);

    let quoteContactsResult: string[] = [];
    const quoteContactsData = quotesList?.map(async (node: any) => {
      const quoteContact = await getQuoteContact(node?.id, node?.quoteContact);
      return quoteContact;
    });

    quoteContactsResult = await Promise.all(quoteContactsData);

    let purchaseOrderNumber: string[] = [];
    const purchaseOrderNumberData = quotesList?.map(async (node: any) => {
      const purchaseOrderNumber = await getPurchaseOrderNumber(node?.billingGroupId);
      return purchaseOrderNumber;
    });
    purchaseOrderNumber = await Promise.all(purchaseOrderNumberData);
    setQuotesListLoading(false);

    if (quotesList) {
      const data = quotesList.map(async (node: any, idx: number) => {
        node.shipToObject = {
          ...node.shipToObject,
          name: node.shipToObject?.displayName,
        };

        const csvDetails = [
          node?.id ?? '-',
          node?.quoteNumber ?? '-',
          node?.name ?? '-',
          node?.status ?? '-',
          `${node?.currency ?? Currency.USD} ${getTotalPartnerMarginAmount(
            node,
            DiscountType.PARTNER_MARGIN,
            null,
            node?.currency
          )}`,
          node?.createdDate ?? '-',
          node?.expiryDate ?? '-',
          node?.updatedDate ?? '-',
          node?.total ?? '-',
          node?.shipToObject?.displayName ?? '-',
          formatAddressLabel(node.shipToObject as CommerceAddress) || '-',
          result[idx] ?? '-',
          purchaseOrderNumber[idx] ?? '-',
          quoteContactsResult[idx] ?? '-',
          node?.quoteAuthor ?? '-',
          node?.updatedBy ?? '-',
        ];

        return csvDetails;
      });

      downloadAsCSV({
        fileName: 'Quotes-list',
        header: getColumns(),
        data: await Promise.all(data),
      });
    }
  };

  return { downloadQuotesListCSV, quotesListLoading };
};
